import { createSlice, PayloadAction, ActionReducerMapBuilder } from '@reduxjs/toolkit'
import { PoolingResponseType } from 'features/Order/services/allOrder'
import { OrderResponseType } from 'features/Order/utils/types'
import { withLoadingReducer } from 'utils/reducerHandler'
import { getOrderListV2Thunk, postStagingAreaThunk } from './thunk'

export type InitialStateType = {
  dialogState:
    | 'CLOSED'
    | 'PRINT_RESI_DIALOG'
    | 'OPEN_INPUT_ORDER_ID'
    | 'OPEN_HEADSTART_POPUP'
    | 'PACKING_DETAIL_POPUP'
    | 'POOLING_DIALOG'
    | 'UPDATE_STATUS_POPUP'
    | 'KITCHEN_POPUP'
    | 'OPEN_AUTH_PIN_POPUP'
    | 'OPEN_AUTH_PIN_CHANGE_PICKER_POPUP'
    | 'COMPLETE_ORDER_KITCHEN'
    | 'BYPASS_SCAN_RESI'
    | 'BYPASS_PRINT_RESI'
    | 'UNPOOL_DIALOG'
    | 'KIRIM_BERSAMAAN_DIALOG'
    | 'REASSIGN_PICKER'
    | 'MOVE_ORDER_TO_WAITING_DRIVER'
    | 'KIRIM_BERSAMAAN_DIALOG'
  showStaffPopup: boolean
  showPopupStagingArea: boolean
  isLoadingStaging: boolean
  isLoading: boolean
  orders: OrderResponseType['orders']
  selectedOrder: OrderResponseType['orders'][number] | null
  printReceiptOrderId: string | null
  locations: {
    location_id: number
    location_name: string
    location_type: string
  }[]
  selectedPoolOrder: OrderResponseType['orders']
  mainPoolOrder: {
    pool_id?: number
    pool_number: string | number
    orders: PoolingResponseType[]
  }[]
  pagination: {
    limit: number
    has_next: boolean
    has_prev: boolean
    page: number
  }
  show3plPopup: boolean
  orderSelected: {
    order_id: number
    order_type: string
  }
  threeplLogHistoryEnabled: boolean
}

const initialState: InitialStateType = {
  dialogState: 'CLOSED',
  showStaffPopup: false,
  showPopupStagingArea: false,
  isLoadingStaging: false,
  isLoading: false,
  orders: [],
  locations: [],
  selectedOrder: null,
  printReceiptOrderId: null,
  selectedPoolOrder: [],
  mainPoolOrder: [],
  pagination: {
    limit: 20,
    has_next: false,
    has_prev: false,
    page: 1,
  },
  show3plPopup: false,
  orderSelected: {
    order_id: 0,
    order_type: '',
  },
  threeplLogHistoryEnabled: false,
}

const ongoingOrderSlice = createSlice({
  name: 'ongoingOrders',
  initialState,
  reducers: {
    setShowPopupStagingArea: (state, action: PayloadAction<boolean>) => {
      state.showPopupStagingArea = action.payload
    },
    setDialogState: (state, action) => {
      state.dialogState = action.payload
    },
    setSelectedOrder: (state, action: PayloadAction<InitialStateType['selectedOrder']>) => {
      state.selectedOrder = action.payload
    },
    setPrintReceiptOrderId: (
      state,
      action: PayloadAction<InitialStateType['printReceiptOrderId']>,
    ) => {
      state.printReceiptOrderId = action.payload
    },
    setPoolOrder: (state, action: PayloadAction<OrderResponseType['orders'][number]>) => {
      const prevOrder = state.selectedPoolOrder
      const isExist = prevOrder.find((order) => order.order_id === action.payload.order_id)

      if (isExist) {
        state.selectedPoolOrder = prevOrder.filter(
          (order) => order.order_id !== action.payload.order_id,
        )
      } else {
        state.selectedPoolOrder.push(action.payload)
      }
    },
    clearPoolOrders: (state) => {
      state.selectedPoolOrder = []
    },
    setMainPoolOrders: (state, action: PayloadAction<InitialStateType['mainPoolOrder']>) => {
      state.mainPoolOrder = action.payload
    },
    setSelectedPoolOrders: (
      state,
      action: PayloadAction<InitialStateType['selectedPoolOrder']>,
    ) => {
      state.selectedPoolOrder = action.payload
    },
    resetPoolingState: (state) => {
      state.selectedPoolOrder = []
      state.mainPoolOrder = []
    },
    resetOrder: (state) => {
      state.orders = []
    },
    setShow3plPopup: (state, action: PayloadAction<boolean>) => {
      state.show3plPopup = action.payload
    },
    setOrderSelected: (state, action) => {
      state.orderSelected = action.payload
    },
    setThreeplLogHistoryEnabled: (state, action: PayloadAction<boolean>) => {
      state.threeplLogHistoryEnabled = action.payload
    },
  },
  extraReducers: withLoadingReducer(
    (builder: ActionReducerMapBuilder<InitialStateType>) => {
      builder.addCase(postStagingAreaThunk.fulfilled, (state) => {
        state.isLoadingStaging = false
      })
      builder.addCase(postStagingAreaThunk.rejected, (state) => {
        state.isLoadingStaging = false
      })
      builder.addCase(postStagingAreaThunk.pending, (state) => {
        state.isLoadingStaging = true
      })
      builder.addCase(getOrderListV2Thunk.fulfilled, (state, action) => {
        state.isLoading = false
        if (action.payload) {
          state.orders = action.payload.orders
          if (action.payload.pagination) {
            state.pagination = action.payload.pagination
          }
        }
      })
    },
    [postStagingAreaThunk, getOrderListV2Thunk],
  ),
})

export const {
  setShowPopupStagingArea,
  setDialogState,
  setSelectedOrder,
  setPrintReceiptOrderId,
  setPoolOrder,
  clearPoolOrders,
  setMainPoolOrders,
  setSelectedPoolOrders,
  resetPoolingState,
  resetOrder,
  setShow3plPopup,
  setOrderSelected,
  setThreeplLogHistoryEnabled,
} = ongoingOrderSlice.actions
export default ongoingOrderSlice.reducer
